import { INetworkResult } from '@/coreDomainsModule/networkResult';
import { axiosErrorHandler } from '../axios/axiosError';
import { customAxiosInstance } from '../axios/axiosInstance';
import {
  BaseBusinessApi,
  BaseBusinessMemoBaseBusinessIdPutRequest,
  BaseBusinessUserIdPutRequest,
  BulkPutBaseBusinessRequestUpdateInfos,
  BusinessMemoApi,
  BusinessUserIdApi,
  GetBaseBusinessFilterItemObjectParameter,
  GetBaseBusinessSuccessResponse,
  SortType,
  SuccessResponse,
  UseFilterItem,
} from '../axios/generated/baseBusinessManage';
import { IBaseBusinessRepository } from '../interfaces/IBaseBusinessRepository';
import { IError } from '../interfaces/IError';
import { AxiosResponse } from 'axios';

/**
 * 取引テーブルのリポジトリ
 */
export class BaseBusinessRepository implements IBaseBusinessRepository {
  /**
   * 取引テーブルの取得
   * @param sortType - ソートタイプ
   * @param limit - 取得件数
   * @param offset - 取得開始位置
   * @param filterItem - フィルター
   * @param sortTarget - ソート対象
   * @param filterItemObject - フィルターの中で、Objectで渡す必要のあるもの
   */
  async getBaseBusiness(
    sortType: SortType,
    limit: number,
    offset: number,
    filterItem: UseFilterItem,
    sortTarget: string,
    filterItemObject?: GetBaseBusinessFilterItemObjectParameter
  ): Promise<INetworkResult<GetBaseBusinessSuccessResponse, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new BaseBusinessApi(undefined, undefined, axiosInstance);

    // APIを叩く
    try {
      const apiResponse = await api.getBaseBusiness(sortType, limit, offset, filterItem, sortTarget, filterItemObject);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: apiResponse.data } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 取引テーブル上のメモを追加/変更する
   * @param businessId - 追加/変更する取引ID
   * @param baseBusinessMemoBaseBusinessIdPutRequest - リクエスト（メモの中身）
   */
  async putBaseBusinessMemo(
    businessId: string,
    baseBusinessMemoBaseBusinessIdPutRequest: BaseBusinessMemoBaseBusinessIdPutRequest
  ): Promise<INetworkResult<string, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new BusinessMemoApi(undefined, undefined, axiosInstance);
    // APIを叩く
    try {
      const apiResponse = await api.baseBusinessMemoBaseBusinessIdPut(businessId, baseBusinessMemoBaseBusinessIdPutRequest);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: apiResponse.data.code as unknown as string } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 取引テーブル上のメモを削除する
   * @param businessId - 追加/変更する取引ID
   */
  async deleteBaseBusinessMemo(businessId: string): Promise<INetworkResult<string, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new BusinessMemoApi(undefined, undefined, axiosInstance);
    // APIを叩く
    try {
      const apiResponse = await api.baseBusinessMemoBaseBusinessIdDelete(businessId);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: apiResponse.data.code as unknown as string } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 取引テーブル上の担当者IDを更新する
   * @param baseBusinessUserIdPutRequest - 更新情報
   */
  async updateBaseBusinessUserId(baseBusinessUserIdPutRequest: BaseBusinessUserIdPutRequest): Promise<INetworkResult<void, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new BusinessUserIdApi(undefined, undefined, axiosInstance);
    // APIを叩く
    try {
      const apiResponse = await api.baseBusinessUserIdPut(baseBusinessUserIdPutRequest);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: undefined } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 関連取引情報の一括編集
   * @param targetIds - 対象のID
   * @param updateInfos - 更新情報
   * @param nullKeys - NULLにするキーリスト
   */
  async bulkPut(
    targetIds: string[],
    updateInfos: BulkPutBaseBusinessRequestUpdateInfos,
    nullKeys?: string[]
  ): Promise<INetworkResult<void, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new BaseBusinessApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse: AxiosResponse<SuccessResponse, IError> = await api.baseBusinessBulkPut({ targetIds, updateInfos, nullKeys });
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: undefined } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }
}
